/**
 * @file GenerateReportPageSkeleton.tsx
 *
 * @description Provides a loading skeleton placeholder for the Report Generation page.
 * This component is displayed during data loading to improve perceived performance
 * and reduce layout shifts when the actual content renders.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import { Box, Skeleton, Card, CardContent } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * @function GenerateReportPageSkeleton
 *
 * @description This component creates placeholder UI elements that mimic the structure of the
 * actual Report Generation page body, giving officers a preview of the layout while
 * data is being loaded. The page header and wizard stepper are rendered outside this
 * skeleton, so it only mirrors the two-column body:
 *
 * - Left panel (md 8): video evidence card with a 16:9 player and a 3-column metadata row
 * - Right panel (md 4): report configuration card with template selector and generate action
 *
 * @returns {JSX.Element} A skeleton placeholder UI for the Generate Report page body
 */
export const GenerateReportPageSkeleton: React.FC = () => {
  return (
    <Grid
      container
      spacing={3}
      direction="column"
      sx={{ width: "100%", alignItems: "flex-start" }}
    >
      <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
        {/* Main Content - two column body */}
        <Grid container spacing={2} sx={{ width: "100%" }}>
          {/* Left Side - VideoEvidenceSection */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card elevation={0} sx={{ height: "100%", width: "100%", borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                {/* Section header: numbered badge + title */}
                <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="text" width={220} height={28} />
                </Box>
                {/* Divider placeholder */}
                <Skeleton variant="rectangular" height={1} sx={{ mb: 3 }} />

                {/* Video player - 16:9 aspect ratio */}
                <Skeleton
                  variant="rounded"
                  width="100%"
                  sx={{ aspectRatio: "16/9", mb: 2 }}
                />

                {/* Video metadata - 3 equal columns (RECORDED / UPLOADED / ID) */}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {[0, 1, 2].map((i) => (
                    <Grid size={{ xs: 12, sm: 4 }} key={i}>
                      <Box
                        sx={{
                          backgroundColor: "action.hover",
                          p: 1.5,
                          borderRadius: 2,
                          height: "100%",
                        }}
                      >
                        <Skeleton variant="text" width={80} height={16} sx={{ mb: 0.5 }} />
                        <Skeleton variant="text" width="70%" height={20} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel - ReportConfigSection */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card elevation={0} sx={{ height: "100%", width: "100%", borderRadius: 3 }}>
              <CardContent sx={{ p: 3, display: "flex", flexDirection: "column" }}>
                {/* Section header: numbered badge + title */}
                <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="text" width={200} height={28} />
                </Box>
                {/* Divider placeholder */}
                <Skeleton variant="rectangular" height={1} sx={{ mb: 3 }} />

                {/* Report template selector */}
                <Skeleton variant="rounded" height={56} sx={{ mb: 3 }} />

                {/* Narrative example area */}
                <Skeleton variant="rounded" height={160} sx={{ mb: 3 }} />

                {/* Generate action button */}
                <Skeleton variant="rounded" height={48} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
